/**
 * D-1103-VB-2 (Eli, 17 Oct 2026: "the office marks it after a call or email") — a group outing's volunteers who hold a
 * place, on Companion & Wellness, each with "Can't come". A volunteer called to say they cannot come: the office presses
 * it (asks once more), the line is kept as Withdrawn, the place opens, and the page offers the next on standby or an
 * "Email all … spots open" again. Names only: an outing never carries a tenant list. Nothing is emailed by this press.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OutingView } from '@shared/volunteers-api';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { outingVolunteerWithdrawReq } from '@/features/events/api183e';
import { errorText } from '@/features/people/api';
import { useVolunteerRefresh } from './api';

export function OutingGoing1103({ outing }: { outing: OutingView }) {
  const { t } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useVolunteerRefresh();
  const [asking, setAsking] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const going = outing.interested.filter((i) => !i.standby);
  if (!going.length) return null;
  const id = outing.eventId;
  const press = async (volunteerId: string, name: string) => {
    setBusy(true);
    try {
      const r = await outingVolunteerWithdrawReq(id, volunteerId);
      const m = `${name}: ${r.message}`;
      toast(m);
      announce(m);
      setAsking(null);
    } catch (x) {
      const m = errorText(x);
      toast(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
      refresh();
    }
  };
  return (
    <details className="vb1103-going" data-testid={`outing-going-${id}`}>
      <summary>{t('vb1103.going', { count: going.length })}</summary>
      <ul>
        {going.map((g) => (
          <li key={g.volunteerId} data-testid={`outing-going-${id}-${g.volunteerId}`}>
            <b>{g.name}</b>{' '}
            {asking === g.volunteerId ? (
              <>
                <span role="alert">{t('vb1103.cantAsk', { name: g.name })}</span>{' '}
                <Button variant="secondary" size="sm" disabled={busy} onClick={() => void press(g.volunteerId, g.name)} data-testid={`outing-cant-yes-${g.volunteerId}`}>
                  {t('vb1103.cantYes')}
                </Button>{' '}
                <Button variant="secondary" size="sm" disabled={busy} onClick={() => setAsking(null)} data-testid={`outing-cant-no-${g.volunteerId}`}>
                  {t('vb1103.cantNo')}
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" disabled={busy} aria-label={t('vb1103.cantAria', { name: g.name, outing: outing.name })} onClick={() => setAsking(g.volunteerId)} data-testid={`outing-cant-${g.volunteerId}`}>
                {t('vb1103.cant')}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}
